import React, { useState, useEffect } from 'react';
import { FiPhone, FiMessageSquare, FiImage, FiSend, FiMail, FiVideo, FiFile, FiBell, FiPhoneCall, FiSettings, FiExternalLink, FiWifi, FiBattery, FiCheck, FiChevronLeft } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  dark: '#0A1628',
  glass: 'rgba(0,180,216,0.08)',
};

export const TYPE_META = {
  sms: { label: 'SMS', icon: FiPhone, color: '#00B4D8', gradient: 'linear-gradient(135deg, #00B4D8, #0096C7)' },
  whatsapp: { label: 'WhatsApp', icon: FiMessageSquare, color: '#25D366', gradient: 'linear-gradient(135deg, #25D366, #128C7E)' },
  whatsapp_image: { label: 'WhatsApp Image', icon: FiImage, color: '#25D366', gradient: 'linear-gradient(135deg, #25D366, #128C7E)' },
  whatsapp_video: { label: 'WhatsApp Video', icon: FiVideo, color: '#25D366', gradient: 'linear-gradient(135deg, #25D366, #128C7E)' },
  whatsapp_document: { label: 'WhatsApp Doc', icon: FiFile, color: '#25D366', gradient: 'linear-gradient(135deg, #25D366, #128C7E)' },
  rcs: { label: 'RCS', icon: FiMessageSquare, color: '#00B4D8', gradient: 'linear-gradient(135deg, #00B4D8, #0077B6)' },
  status_messaging: { label: 'Status', icon: FiSend, color: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
  newsletter: { label: 'Newsletter', icon: FiMail, color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  emailer: { label: 'Emailer', icon: FiMail, color: '#d97706', gradient: 'linear-gradient(135deg, #d97706, #b45309)' },
  push_notification: { label: 'Push', icon: FiBell, color: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1, #4F46E5)' },
  voice_call: { label: 'Voice Call', icon: FiPhoneCall, color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)' },
  custom: { label: 'Custom', icon: FiSettings, color: '#64748b', gradient: 'linear-gradient(135deg, #64748b, #475569)' }
};

/* ══ Animated Floating Particles ══ */
const FloatingParticles = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
    {[...Array(6)].map((_, i) => (
      <div key={i} style={{
        position: 'absolute',
        width: 4 + Math.random() * 6,
        height: 4 + Math.random() * 6,
        borderRadius: '50%',
        background: `rgba(0,180,216,${0.15 + Math.random() * 0.2})`,
        left: `${10 + Math.random() * 80}%`,
        top: `${10 + Math.random() * 80}%`,
        animation: `float${i % 3} ${3 + Math.random() * 4}s ease-in-out infinite`,
        animationDelay: `${Math.random() * 2}s`,
      }} />
    ))}
    <style>{`
      @keyframes float0 { 0%, 100% { transform: translateY(0) translateX(0); } 50% { transform: translateY(-15px) translateX(5px); } }
      @keyframes float1 { 0%, 100% { transform: translateY(0) translateX(0); } 50% { transform: translateY(10px) translateX(-8px); } }
      @keyframes float2 { 0%, 100% { transform: translateY(0) translateX(0); } 50% { transform: translateY(-8px) translateX(-5px); } }
    `}</style>
  </div>
);

/* ══ Premium Phone Shell ══ */
const PhoneShell = ({ children, variant = 'light', showAnimation = true }) => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () => setTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, []);

  const isDark = variant === 'dark';
  
  return (
    <div style={{ display: 'flex', justifyContent: 'center', perspective: '1000px' }}>
      <div style={{
        width: 340,
        background: isDark 
          ? 'linear-gradient(145deg, #1a1a2e 0%, #0f0f1a 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 42,
        padding: '8px',
        boxShadow: isDark
          ? '0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08) inset, 0 0 60px rgba(0,180,216,0.1)'
          : '0 30px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05), 0 0 60px rgba(0,180,216,0.08)',
        position: 'relative',
        transform: 'rotateX(2deg)',
        transformStyle: 'preserve-3d',
      }}>
        {/* Animated glow ring */}
        <div style={{
          position: 'absolute', inset: -2, borderRadius: 44, zIndex: -1,
          background: `linear-gradient(45deg, ${THEME.primary}40, transparent, ${THEME.secondary}40, transparent)`,
          animation: 'glowRotate 6s linear infinite',
          filter: 'blur(8px)',
        }} />
        <style>{`@keyframes glowRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

        {/* Dynamic Island / Notch */}
        <div style={{
          width: 120, height: 32, background: isDark ? '#000' : '#1a1a2e',
          borderRadius: 20, margin: '0 auto 6px', position: 'relative', zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)',
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#333', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)' }} />
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: THEME.primary, animation: 'pulse 2s ease-in-out infinite', boxShadow: `0 0 10px ${THEME.primary}` }} />
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.9); } }`}</style>

        {/* Screen */}
        <div style={{
          borderRadius: 34, overflow: 'hidden', position: 'relative',
          background: isDark ? '#0f0f1a' : '#fff',
        }}>
          {/* Status Bar */}
          <div style={{
            padding: '8px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.9)',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#1a1a2e' }}>{time}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <FiWifi size={12} style={{ color: isDark ? '#fff' : '#1a1a2e' }} />
              <div style={{ display: 'flex', gap: 1 }}>
                {[4,6,8,10].map((h, i) => (
                  <div key={i} style={{ width: 3, height: h, borderRadius: 1, background: isDark ? '#fff' : '#1a1a2e', opacity: i < 3 ? 1 : 0.3 }} />
                ))}
              </div>
              <FiBattery size={14} style={{ color: isDark ? '#fff' : '#1a1a2e' }} />
            </div>
          </div>

          {/* Content */}
          <div style={{ position: 'relative' }}>
            {showAnimation && <FloatingParticles />}
            {children}
          </div>
        </div>

        {/* Home Indicator */}
        <div style={{
          width: 100, height: 4, borderRadius: 2, margin: '8px auto 4px',
          background: isDark ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
        }} />
      </div>
    </div>
  );
};

/* ══ Typing Indicator Animation ══ */
const TypingIndicator = () => (
  <div style={{ display: 'flex', gap: 4, padding: '12px 16px', background: '#e5e5ea', borderRadius: 20, width: 'fit-content' }}>
    {[0, 1, 2].map(i => (
      <div key={i} style={{
        width: 8, height: 8, borderRadius: '50%', background: '#8e8e93',
        animation: 'bounce 1.4s ease-in-out infinite',
        animationDelay: `${i * 0.16}s`,
      }} />
    ))}
    <style>{`@keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }`}</style>
  </div>
);

/* ══ Message Bubble with Animation ══ */
const MessageBubble = ({ children, variant = 'received', delay = 0 }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }, [delay]);

  const isReceived = variant === 'received';
  return (
    <div style={{
      opacity: show ? 1 : 0,
      transform: show ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)',
      transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      maxWidth: '85%',
      alignSelf: isReceived ? 'flex-start' : 'flex-end',
    }}>
      {children}
    </div>
  );
};

export default function MessageTemplatePreview({ type, sched, campaignName }) {
  const meta = TYPE_META[type] || TYPE_META.sms;
  const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const Icon = meta.icon;

  // ═══ SMS ═══
  if (type === 'sms') {
    return (
      <PhoneShell variant="dark">
        {/* Header */}
        <div style={{
          background: 'linear-gradient(180deg, rgba(0,180,216,0.15) 0%, transparent 100%)',
          padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ fontSize: 10, color: THEME.secondary, letterSpacing: 2, fontWeight: 600, textTransform: 'uppercase' }}>Messages</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 12, background: meta.gradient,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 20px ${THEME.primary}50`,
            }}>
              <Icon size={18} style={{ color: '#fff' }} />
            </div>
            {campaignName || sched.senderId || 'SENDER'}
          </div>
        </div>

        {/* Messages */}
        <div style={{ padding: '20px 14px', minHeight: 200, display: 'flex', flexDirection: 'column', gap: 12, background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)' }}>
          <MessageBubble delay={200}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(0,180,216,0.2) 0%, rgba(0,180,216,0.1) 100%)',
              borderRadius: '20px 20px 20px 6px', padding: '14px 18px',
              border: '1px solid rgba(0,180,216,0.2)',
              boxShadow: '0 4px 20px rgba(0,180,216,0.1)',
            }}>
              <div style={{ fontSize: 15, color: '#fff', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {sched.messageContent || 'Message content...'}
              </div>
              {sched.footerText && (
                <div style={{ fontSize: 11, color: THEME.secondary, marginTop: 8, opacity: 0.8 }}>{sched.footerText}</div>
              )}
            </div>
            <div style={{ fontSize: 10, color: THEME.muted, marginTop: 6, paddingLeft: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              {time}
              <FiCheck size={10} style={{ color: THEME.primary }} />
              <FiCheck size={10} style={{ color: THEME.primary, marginLeft: -6 }} />
            </div>
          </MessageBubble>
        </div>

        {/* Input */}
        <div style={{ padding: '12px 14px', background: '#1a1a2e', display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{
            flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 24, padding: '12px 18px',
            fontSize: 14, color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.1)',
          }}>
            Type a message...
          </div>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', background: meta.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 20px ${THEME.primary}50`,
            animation: 'sendPulse 2s ease-in-out infinite',
          }}>
            <FiSend size={18} style={{ color: '#fff', transform: 'rotate(-30deg)' }} />
          </div>
        </div>
        <style>{`@keyframes sendPulse { 0%, 100% { box-shadow: 0 4px 20px rgba(0,180,216,0.3); } 50% { box-shadow: 0 4px 30px rgba(0,180,216,0.6); } }`}</style>
      </PhoneShell>
    );
  }

  // ═══ WhatsApp / WhatsApp Image ═══
  if (type === 'whatsapp' || type === 'whatsapp_image') {
    return (
      <PhoneShell variant="light">
        {/* WhatsApp Header */}
        <div style={{
          background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
          padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <FiChevronLeft size={22} style={{ color: '#fff' }} />
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'linear-gradient(135deg, #25D366, #128C7E)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 800, fontSize: 16,
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          }}>
            {(campaignName || sched.senderId || 'W')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>{campaignName || 'WhatsApp Business'}</div>
            <div style={{ color: '#b2f5ea', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', animation: 'pulse 2s infinite' }} />
              online
            </div>
          </div>
        </div>

        {/* Chat */}
        <div style={{
          background: '#e5ddd5',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9c2b5' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          padding: '16px 12px', minHeight: 220, display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {/* Date chip */}
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: 11, color: '#667', background: 'rgba(255,255,255,0.9)',
              borderRadius: 8, padding: '4px 14px', fontWeight: 600,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>TODAY</span>
          </div>

          <MessageBubble delay={300}>
            <div style={{
              background: '#dcf8c6', borderRadius: '4px 16px 16px 16px',
              overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}>
              {sched.headerText && (
                <div style={{ padding: '12px 14px 4px', fontSize: 15, fontWeight: 700, color: '#111' }}>{sched.headerText}</div>
              )}
              {type === 'whatsapp_image' && (
                <div style={{ margin: '8px', borderRadius: 10, overflow: 'hidden', background: '#c8e6c9' }}>
                  {sched.imageUrl ? (
                    <img src={sched.imageUrl} alt="" style={{ width: '100%', height: 180, objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style="height:180px;display:flex;align-items:center;justify-content:center;font-size:48px">🖼️</div>'; }} />
                  ) : (
                    <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FiImage size={48} style={{ color: '#69a36f' }} />
                    </div>
                  )}
                </div>
              )}
              <div style={{ padding: '10px 14px', fontSize: 15, color: '#111', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {sched.messageContent || 'Message...'}
              </div>
              {sched.footerText && <div style={{ padding: '0 14px 8px', fontSize: 12, color: '#888' }}>{sched.footerText}</div>}
              <div style={{ padding: '0 14px 10px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#7d9b7e' }}>{time}</span>
                <span style={{ color: '#4fc3f7', display: 'flex' }}>
                  <FiCheck size={12} /><FiCheck size={12} style={{ marginLeft: -6 }} />
                </span>
              </div>
              {sched.buttonText && (
                <div style={{
                  borderTop: '1px solid rgba(0,0,0,0.08)', padding: '14px',
                  textAlign: 'center', background: 'rgba(255,255,255,0.5)',
                }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    color: '#039be5', fontSize: 14, fontWeight: 600,
                  }}>
                    <FiExternalLink size={14} /> {sched.buttonText}
                  </div>
                </div>
              )}
            </div>
          </MessageBubble>
        </div>

        {/* Input */}
        <div style={{ padding: '10px 12px', background: '#f0f0f0', display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ flex: 1, background: '#fff', borderRadius: 24, padding: '10px 16px', fontSize: 14, color: '#999', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            Type a message
          </div>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiSend size={18} style={{ color: '#fff', transform: 'rotate(-30deg)' }} />
          </div>
        </div>
      </PhoneShell>
    );
  }

  // ═══ Push Notification ═══
  if (type === 'push_notification') {
    return (
      <PhoneShell variant="dark">
        {/* Lock Screen */}
        <div style={{
          background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
          padding: '30px 16px', minHeight: 300, position: 'relative',
        }}>
          {/* Large Time */}
          <div style={{ textAlign: 'center', marginBottom: 30 }}>
            <div style={{ fontSize: 64, fontWeight: 200, color: '#fff', letterSpacing: -2 }}>
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>

          {/* Notification */}
          <MessageBubble delay={400}>
            <div style={{
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)',
              borderRadius: 20, padding: 14, border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: meta.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  boxShadow: '0 4px 15px rgba(99,102,241,0.4)',
                }}>
                  <FiBell size={22} style={{ color: '#fff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{sched.senderId || 'App Name'}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>now</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
                    {sched.notificationTitle || sched.headerText || 'Notification'}
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                    {sched.messageContent?.substring(0, 100) || 'Notification body...'}
                    {sched.messageContent?.length > 100 && '...'}
                  </div>
                </div>
              </div>
            </div>
          </MessageBubble>
        </div>

        {/* Swipe hint */}
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.3)', margin: '0 auto', animation: 'swipeHint 2s ease-in-out infinite' }} />
        </div>
        <style>{`@keyframes swipeHint { 0%, 100% { transform: translateY(0); opacity: 0.5; } 50% { transform: translateY(-5px); opacity: 1; } }`}</style>
      </PhoneShell>
    );
  }

  // ═══ Voice Call ═══
  if (type === 'voice_call') {
    return (
      <PhoneShell variant="dark">
        <div style={{
          background: 'linear-gradient(180deg, #0d9488 0%, #065f46 100%)',
          padding: '40px 20px', textAlign: 'center', minHeight: 320,
        }}>
          {/* Caller Avatar with Rings */}
          <div style={{ position: 'relative', width: 100, height: 100, margin: '0 auto 20px' }}>
            <div style={{
              position: 'absolute', inset: -20, borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.1)',
              animation: 'ringPulse 2s ease-out infinite',
            }} />
            <div style={{
              position: 'absolute', inset: -10, borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.2)',
              animation: 'ringPulse 2s ease-out infinite 0.3s',
            }} />
            <div style={{
              width: 100, height: 100, borderRadius: '50%',
              background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            }}>
              <FiPhoneCall size={40} style={{ color: '#fff' }} />
            </div>
          </div>
          <style>{`@keyframes ringPulse { 0% { transform: scale(1); opacity: 0.4; } 100% { transform: scale(1.5); opacity: 0; } }`}</style>

          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
            {sched.senderId || 'Caller ID'}
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', marginBottom: 30 }}>
            Incoming Voice Call
          </div>

          {/* Script Preview */}
          <div style={{
            background: 'rgba(255,255,255,0.1)', borderRadius: 16, padding: 16,
            textAlign: 'left', marginBottom: 30,
          }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
              Voice Script
            </div>
            <div style={{ fontSize: 14, color: '#fff', lineHeight: 1.6, fontStyle: 'italic' }}>
              "{sched.voiceScript || sched.messageContent || 'Hello, this is an automated call...'}"
            </div>
          </div>

          {/* Call Actions */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 40 }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%', background: '#ef4444',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              boxShadow: '0 6px 25px rgba(239,68,68,0.5)',
            }}>
              <FiPhone size={24} style={{ color: '#fff', transform: 'rotate(135deg)' }} />
            </div>
            <div style={{
              width: 60, height: 60, borderRadius: '50%', background: '#22c55e',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              boxShadow: '0 6px 25px rgba(34,197,94,0.5)',
              animation: 'callPulse 1.5s ease-in-out infinite',
            }}>
              <FiPhone size={24} style={{ color: '#fff' }} />
            </div>
          </div>
          <style>{`@keyframes callPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }`}</style>
        </div>
      </PhoneShell>
    );
  }

  // ═══ Email / Newsletter ═══
  if (type === 'newsletter' || type === 'emailer') {
    return (
      <PhoneShell variant="light">
        {/* Email Header */}
        <div style={{
          background: meta.gradient, padding: '20px 16px', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>From: {sched.senderId || 'newsletter@brand.com'}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginTop: 8 }}>
            {sched.subject || 'Newsletter Subject'}
          </div>
        </div>

        {/* Email Body */}
        <div style={{ padding: '20px 16px', background: '#fff', minHeight: 200 }}>
          <MessageBubble delay={200}>
            <div>
              {sched.headerText && (
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111', marginBottom: 12 }}>{sched.headerText}</div>
              )}
              {sched.imageUrl && (
                <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                  <img src={sched.imageUrl} alt="" style={{ width: '100%', height: 160, objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
              <div style={{ fontSize: 14, color: '#444', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {sched.messageContent || 'Email content...'}
              </div>
              {sched.buttonText && (
                <div style={{
                  marginTop: 20, textAlign: 'center',
                }}>
                  <span style={{
                    display: 'inline-block', background: meta.gradient,
                    color: '#fff', padding: '12px 32px', borderRadius: 30,
                    fontSize: 14, fontWeight: 600,
                    boxShadow: `0 6px 25px ${meta.color}50`,
                  }}>
                    {sched.buttonText}
                  </span>
                </div>
              )}
              {sched.footerText && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee', fontSize: 12, color: '#999', textAlign: 'center' }}>
                  {sched.footerText}
                </div>
              )}
            </div>
          </MessageBubble>
        </div>
      </PhoneShell>
    );
  }

  // ═══ Default / RCS / Status / Custom ═══
  return (
    <PhoneShell variant="dark">
      {/* Header */}
      <div style={{
        background: meta.gradient, padding: '16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={22} style={{ color: '#fff' }} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{campaignName || sched.senderId || 'Message'}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{meta.label}</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '20px 14px', minHeight: 200, background: '#0f0f1a' }}>
        <MessageBubble delay={300}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
            borderRadius: 16, padding: 16, border: '1px solid rgba(255,255,255,0.1)',
          }}>
            {sched.headerText && (
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 10 }}>{sched.headerText}</div>
            )}
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {sched.messageContent || 'Message content...'}
            </div>
            {sched.footerText && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 12 }}>{sched.footerText}</div>
            )}
            {sched.buttonText && (
              <div style={{ marginTop: 16 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: meta.gradient, color: '#fff',
                  padding: '10px 20px', borderRadius: 24, fontSize: 13, fontWeight: 600,
                }}>
                  <FiExternalLink size={14} /> {sched.buttonText}
                </span>
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
            {time}
            <FiCheck size={10} /><FiCheck size={10} style={{ marginLeft: -6 }} />
          </div>
        </MessageBubble>
      </div>
    </PhoneShell>
  );
}
